import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLoader } from '../../../context/LoaderContext';
import { DetailBox, PaginatedTable, TitleBar, Total } from '../../common';
import {
  BILLS_DETAILS_COLUMN,
  TITLES,
  ROUTES,
  POPUP_TYPE,
  DATA_FIELD,
  BUTTON,
  LOCAL_STORAGE,
} from '../../../constants';
import {
  billDetailsCompiler,
  billsDetailBox,
  popup,
  toInputDate,
  reducedCalc,
  getLocalStorageItem,
} from '../../../utils';
import { useDebounce } from '../../../hooks';
import { deleteBillLine, printBillPackingList, updateBill, updateBillLine, vendorBill } from '../../../service';
import { Button, PopUpBox } from '../../shared';
import ContainerThreadBoxEntry from '../../common/ContainerThreadBoxEntry';

export const BillsDetails = () => {
  const { state } = useLocation(); // Only used for bill ID
  const { setLoading } = useLoader();
  const navigate = useNavigate();

  const [billsData, setBillsData] = useState([]);
  const [lineData, setLineData] = useState([]);
  const [paginatedLineData, setPaginatedLineData] = useState([]);
  const [value, setValue] = useState(toInputDate());
  const [showSave, setShowSave] = useState(false);
  const [popUp, setPopUp] = useState(0);
  const [popUpData, setPopUpData] = useState('');
  const [costViewOpen, setCostViewOpen] = useState(false);
  const [paginationData, setPaginationData] = useState({});
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const PAGE_SIZE = 20;

  // Set value from API response
  useEffect(() => {
    if (billsData?.invoice_date) {
      setValue(billsData.invoice_date);
    }
  }, [billsData]);

  useEffect(() => {
    const totalRecords = lineData.length;
    const startIndex = (pageNumber - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    const slicedData = lineData.slice(startIndex, endIndex);
    setPaginatedLineData(slicedData);

    setPaginationData({
      page: pageNumber,
      start: totalRecords === 0 ? 0 : startIndex + 1,
      end: totalRecords === 0 ? 0 : Math.min(endIndex, totalRecords),
      total_records: totalRecords,
    });
  }, [pageNumber, lineData]);

  const column = BILLS_DETAILS_COLUMN.filter((col) =>
    state?.state !== 'draft' ? col.name !== 'Action' : true,
  );

  const qty = reducedCalc(lineData, DATA_FIELD.QTY);

  const fetchBills = useDebounce(() => {
    if (!state?.id) return navigate(ROUTES.BILLS);
    setIsLoading(true);
    vendorBill({ data: { bill_ids: state.id, page_number: pageNumber } })
      .then((response) => {
        setIsLoading(false);
        if (response?.data?.records?.length > 0) {
          setBillsData(response.data.records[0]);
          setLineData(response.data.records[0]?.invoice_line_ids);
          setPaginationData(response.data.params);
        } else {
          setBillsData([]);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  }, 400);

  useEffect(() => {
    fetchBills();
  }, []);

  useEffect(() => {
    if (!costViewOpen) {
      setIsLoading(true);
      fetchBills();
    }
  }, [costViewOpen]);

  const handleLineUpdate = (updatedData, vendor_cost_line_id) => {
    updateBillLine({
      data: { vendor_cost_line_id, data: [updatedData] },
    })
      .then((res) => {
        popup('success', res.message);
        fetchBills();
      })
      .catch(() => setLoading(false));
  };

  const handleDeleteLine = (vendor_cost_line_id) => {
    deleteBillLine({ data: { line_id: vendor_cost_line_id } })
      .then((res) => {
        popup('success', res.message);
        fetchBills();
      })
      .catch(() => setLoading(false));
  };

  const handleDateChange = useDebounce(() => {
    updateBill({
      data: {
        bill_id: state?.id,
        bill_date: toInputDate(new Date(value)),
      },
    })
      .then((res) => {
        popup(POPUP_TYPE.SUCCESS, res.message);
        setShowSave(false);
        fetchBills();
      })
      .catch(() => {
        setLoading(false);
      });
  }, 400);

  useEffect(() => {
    if (!state) navigate(ROUTES.BILLS);
  }, [state, navigate]);

  useEffect(() => {
    setShowSave(value !== billsData?.invoice_date);
  }, [value, billsData?.invoice_date]);

  const handlePrintPackingList = async (bill_ids) => {
    if (!bill_ids) {
      return;
    }
    try {
      const response = await printBillPackingList({
        data: { bill_ids }
      });

      if (response?.data) {
        const downloadUrl = response.data;
        window.open(downloadUrl, '_blank');
      } else {
        console.error("Server Error or Invalid Response: ", response);
      }
    } catch (error) {
      console.error("Server Error: ", error);
    }
  }

  return (
    <>
      <TitleBar
        title={[
          { text: TITLES.BILLS, route: ROUTES.BILLS },
          { text: billsData?.draft_number, route: ROUTES.NULL },
        ]}
      />
      {!costViewOpen && (
        <div className="scroll-div">
          <div className="d-flex justify-content-end">
            {billsData?.state === 'draft' && (
              <Button
                className="border-0 blue-btn py-2 px-3 mb-3 me-2"
                clickHandler={() => setCostViewOpen(true)}>
                Add Lines
              </Button>
            )}
            {(getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'fabric' || getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'dyeing'|| getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'clothing') && !isLoading && (
              <Button
                clickHandler={() => handlePrintPackingList(state?.id)}
                className="blue-btn d-flex align-items-center justify-content-center px-3 mb-2"
              >
                {BUTTON.PRINT}
              </Button>
            )}
          </div>
          {!isLoading && <DetailBox
            colCount={1}
            colData={billsData}
            dataCompiler={billsDetailBox}
            value={value}
            setValue={setValue}
            clickHandler={handleDateChange}
            showSave={showSave}
          />}
          <PaginatedTable
            columns={column}
            initialData={paginatedLineData}
            PAGE_SIZE={20}
            colFilter={false}
            compiler={{ use: true, tool: billDetailsCompiler }}
            popUp={popUp}
            setPopUp={setPopUp}
            setPopUpData={setPopUpData}
            paginationData={paginationData}
            setPageNumber={setPageNumber}
            isLoading={isLoading}
          />
          {!isLoading && <Total
            qty={qty}
            total={billsData?.amount_total}
            uom={billsData?.invoice_line_ids?.[0]?.product_uom_id?.name}
            currency_code={billsData?.currency_id?.name}
          />}
        </div>
      )}
      {costViewOpen && (
        <ContainerThreadBoxEntry
          setCostviewOpen={setCostViewOpen}
          state={billsData}
          isBill={true}
          setLineData={setLineData}
        />
      )}
      {popUp === 1 && (
        <PopUpBox openPopUpBox={popUp}>
          <UpdateLine
            setOpenPopUpBox={setPopUp}
            data={popUpData}
            setLineData={setLineData}
            handleLineUpdate={handleLineUpdate}
            handleDeleteLine={handleDeleteLine}
          />
        </PopUpBox>
      )}
    </>
  );
};


const UpdateLine = ({ setOpenPopUpBox, data, handleLineUpdate, handleDeleteLine }) => {
  const [productName, setProductName] = useState(data?.product || '');
  const [quantity, setQuantity] = useState(data?.qty || '');
  const [unitPrice, setUnitPrice] = useState(data?.unit_price || '');

  const closePopUp = () => {
    setOpenPopUpBox(0);
  };

  const handleUpdate = () => {
    const typeMapping = {
      Threads: 'thread',
      Container: 'container',
      'Box and Polybag': 'box_poly',
    };

    const updatedData = {
      name: productName,
      qty: parseFloat(quantity).toFixed(2),
      unit_price: parseFloat(unitPrice).toFixed(2),
      type: typeMapping[data.type] || data.type,
    };
    handleLineUpdate(updatedData, data?.vendor_cost_line_id);
    closePopUp();
  };

  const handleDelete = () => {
    handleDeleteLine(data?.vendor_cost_line_id);
    closePopUp();
  };

  const isFormUnchanged =
    productName === data.product &&
    parseFloat(quantity).toFixed(2) === parseFloat(data.qty).toFixed(2) &&
    parseFloat(unitPrice).toFixed(2) === parseFloat(data.unit_price).toFixed(2);

  const isFormInvalid =
    !productName ||
    !quantity ||
    !unitPrice ||
    parseFloat(quantity) < 0 ||
    parseFloat(unitPrice) < 0;

  return (
    <>
      <div className="d-flex flex-column m-1">
        <h4 className="text-center text-special mb-3 mt-3">{data.type.toUpperCase()}</h4>
        <hr />
        <div className="w-100 modal-body py-0 d-flex flex-column gap-3 justify-content-center align-items-center">
          <div className="d-flex justify-content-between w-100">
            <label className="ms-3">Name</label>
            <input
              type="text"
              className="ps-2 curve-border me-4"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              placeholder="Enter Name"
            />
          </div>
          <div className="d-flex justify-content-between w-100">
            <label className="ms-3">Quantity</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="ps-2 curve-border me-4"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              onWheel={(e) => e.target.blur()}
              placeholder="Enter Quantity"
            />
          </div>
          <div className="d-flex justify-content-between w-100">
            <label className="ms-3">Unit Price</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="ps-2 curve-border me-4"
              value={unitPrice}
              onChange={(e) => setUnitPrice(e.target.value)}
              onWheel={(e) => e.target.blur()}
              placeholder="Enter Unit Price"
            />
          </div>
          <div className="d-flex justify-content-between align-items-center w-100">
            <label className="ms-3">Subtotal</label>
            <div className="ps-2 me-4 border rounded py-2 px-3 bg-light text-dark fw-bold">
              {quantity && unitPrice
                ? Number((parseFloat(quantity) * parseFloat(unitPrice)).toFixed(2))
                : '0.00'}
            </div>
          </div>
        </div>
      </div>
      <hr />
      <div className="pb-3 mt-2 d-flex align-items-center justify-content-end mx-4 modal-btn custom-button">
        <div className="d-flex align-items-center justify-content-start">
          <Button
            clickHandler={handleDelete}
            className="border-0 me-5 delete-btn d-flex align-items-center justify-content-center py-2 px-3">
            Delete
          </Button>
        </div>
        <div className="ms-5 ps-5 w-50 d-flex align-items-center justify-content-between">
          <Button
            clickHandler={handleUpdate}
            className={`border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3 ${isFormUnchanged || isFormInvalid ? 'disabled' : ''
              }
          `}
            disabled={isFormUnchanged || isFormInvalid}>
            {BUTTON.UPDATE}
          </Button>
          <Button
            clickHandler={closePopUp}
            className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
            {BUTTON.CLOSE}
          </Button>
        </div>
      </div>
    </>
  );
};
